import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { usePostDecisionUser } from '@/core/feature-flags'
import { setSentryUser } from '@/core/sentry'
import { EarnedCardsOverlay } from '@/features/collection/components/earned-cards-overlay'
import { useCollectionExposure } from '@/features/collection/hooks/use-collection-exposure'
import { isOnboardingDeferred } from '@/features/onboarding/hooks/use-onboarding-status'
import { owesOnboarding } from '@/features/onboarding/lib/start-rule'
import { useUser } from '@/features/plan/hooks/use-plan-data'
import { isReturnFlowPending } from '@/features/return-flow/gate'
import { useExposureReport } from '@/features/return-flow/hooks/use-exposure-report'
import { HelpPopup } from '@/features/support/components/help-popup'
import { UserNoticeGate } from '@/features/user-notice/components/user-notice-gate'
import { useCoveringDeadEnd } from '@/features/user-notice/hooks/use-covering-dead-end'
import { useUserNoticeDecision } from '@/features/user-notice/hooks/use-user-notice-decision'
import type { UserQuery } from '@/generated/graphql'
import { AnimatedOutlet } from '@/shared/components/animated-outlet'
import { ErrorFallback } from '@/shared/components/error-fallback'
import { BottomNav } from '@/shared/components/layout/navigation/bottom-nav'
import { TopNav } from '@/shared/components/layout/navigation/top-nav'
import { useIsDesktop } from '@/shared/hooks/use-is-desktop'
import { useReportUnsupportedBrowser } from '@/shared/hooks/use-report-unsupported-browser'
import { useShowsCollection } from '@/shared/hooks/use-shows-collection'
import { ProtectedRoute } from './guards/index'

// First path segment as the tab identity, e.g. `/today/practice/x` -> `/today`,
// `/plan/day/x` -> `/plan`. Sub-routes within a tab share a root.
function tabRoot(pathname: string): string {
  return `/${pathname.split('/')[1] ?? ''}`
}

type UserData = UserQuery['me']

// Where this layout is about to send the user, if anywhere. Read by the effect that
// navigates AND by the notice gate that must not ask before routing has settled — one
// function, so the two can never disagree about who is being redirected.
function pendingRedirect(
  me: UserData | undefined,
  activePlanId: string | undefined,
): '/return' | '/onboarding' | null {
  if (!me?.id || !activePlanId || me.isStoic) return null
  // Checked first, and onboarding stands down for it: the return flow is this
  // user's re-onboarding, and the server records it as such.
  if (isReturnFlowPending(me)) return '/return'
  if (isOnboardingDeferred(me.id, activePlanId)) return null
  if (owesOnboarding(me, activePlanId)) return '/onboarding'
  return null
}

export function ProtectedLayout() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const { data } = useUser()
  const userId = data?.me.id
  const activePlanId = data?.me.activePlanId ?? data?.me.availablePlans[0]?.id

  const isDesktop = useIsDesktop()

  useEffect(() => {
    if (userId) setSentryUser(userId)
  }, [userId])

  const redirectTo = pendingRedirect(data?.me, activePlanId)
  // Whether the server's start-flow decision has landed. Read for the notice gate only:
  // the navigation above deliberately runs off the pre-decision copy, because the
  // onboarding redirect is answerable without /config and should not wait for it.
  const decided = Boolean(usePostDecisionUser())

  // Asked here rather than inside the gate, so the screen and the exposure report below read
  // ONE answer. A LAUNCH ask CONSUMES the day's show server-side, and /return and /onboarding
  // live outside this layout, so asking before routing settles spends the nudge on a user
  // about to be sent somewhere the gate never renders. `!redirectTo` alone does not cover
  // /return: `startFlow` is served only after /config, and /config is itself gated on the
  // user query (use-config.ts), so the copy this layout renders from ALWAYS predates the
  // decision and reads as "staying put" for a while. Waiting for the post-decision user is
  // what closes that window; if /config never lands nothing is asked at all — no notice
  // rather than a spent one, and the shell already reports that failure to Sentry.
  const notice = useUserNoticeDecision(decided && !redirectTo, userId)

  // Reaching a plan surface is the exposure — but a surface a payment-method screen is about
  // to cover has not been reached yet, and neither has one whose verdict is still unknown.
  // iOS gets this from routing (a staged notice means MainTabCoordinator, which owns the
  // report, is never built); here it is the same decision, read explicitly. The dead-end is
  // checked separately because it renders from the app root: mid-task answers `clear` here
  // (nothing is asked there) while a verdict some other surface paid for still covers it.
  const coverage = useCoveringDeadEnd()
  const surfaceReached =
    notice.status === 'clear' && coverage.status === 'clear'
  useExposureReport(surfaceReached)
  // Its own surface, reported here for the same reason and at the same moment: whether the
  // Collection's tab is reachable is not what enters someone into its experiment.
  useCollectionExposure(surfaceReached)
  // Eligible people whose browser cannot run the feature — they get the tab and the screen behind
  // it, and are the count a report from that screen alone would miss.
  const showsCollection = useShowsCollection()
  useReportUnsupportedBrowser('collection', surfaceReached && showsCollection)

  useEffect(() => {
    if (!redirectTo) return
    // Carry the search into /return: the DEV `?returnFlow` escape hatch is read
    // again by the return page, and dropping it here would bounce straight back.
    navigate(
      redirectTo === '/return'
        ? `/return${window.location.search}`
        : redirectTo,
      { replace: true },
    )
  }, [redirectTo, navigate])

  // Reload the journey when entering the Today or Map tab — mirrors iOS
  // (MainTabContainerView.onChange(selectedTab) -> reloadToday). The Today hub's
  // current day (currentDayId) is held in the goal-query cache and only rolls
  // forward when that query refetches, so this tab-entry invalidation is what
  // advances the day. Gated on a real tab-root change so opening/closing a
  // practice or day sub-route (same root) never triggers it.
  const prevTabRootRef = useRef(tabRoot(pathname))
  useEffect(() => {
    const from = prevTabRootRef.current
    const to = tabRoot(pathname)
    prevTabRootRef.current = to
    if (from !== to && (to === '/today' || to === '/plan')) {
      queryClient.invalidateQueries({ queryKey: ['journey'] })
    }
  }, [pathname, queryClient])

  return (
    <ProtectedRoute>
      <div className="flex h-full flex-col bg-background">
        <TopNav />
        <div className="flex w-full min-h-0 flex-1 flex-col lg:overflow-y-auto">
          <div
            className="relative min-h-0 flex-1 overflow-hidden lg:overflow-visible"
            style={{ touchAction: 'none' }}
          >
            <Sentry.ErrorBoundary key={pathname} fallback={<ErrorFallback />}>
              <AnimatedOutlet />
            </Sentry.ErrorBoundary>
          </div>
          <div
            className="shrink-0 lg:hidden"
            style={{ touchAction: 'none', overscrollBehavior: 'none' }}
          >
            <BottomNav />
          </div>
        </div>
      </div>
      <EarnedCardsOverlay />
      <UserNoticeGate decision={notice} />
      {isDesktop && <HelpPopup />}
    </ProtectedRoute>
  )
}
